import { on, reducer } from 'ts-action';
import * as ecgActions from '../../ecg/actions/ecg.actions';
import * as undoActions from '../../undo/actions/undo.actions';
import * as rulerActions from '../actions/ruler.actions';
import { UpdateMode } from '../../wave/utils/update.utils';

declare module 'react-redux' {
  interface DefaultRootState {
    ruler: RulerState;
  }
}

type UpdatedKeys = keyof Pick<DefaultRulerState, 'start' | 'end'>;

export interface BaseRulerState {
  start: number;
  end: number;
  updateMode: null | UpdateMode;
}

export interface DefaultRulerState extends BaseRulerState {
  updateMode: null;
}

export interface BoundRulerState extends BaseRulerState {
  updateMode: 'boundary';
  boundary: UpdatedKeys;
}

export interface MoveRulerState extends BaseRulerState {
  updateMode: 'move';
  previousTimestamp: number;
}

export type RulerState = DefaultRulerState | BoundRulerState | MoveRulerState;

const initialStateFactory = (): RulerState => ({
  start: 0,
  end: 0,
  updateMode: null,
});

export default reducer(
  initialStateFactory(),
  on(ecgActions.clean, () => initialStateFactory()),
  on(
    rulerActions.stop,
    ({ start, end }): RulerState => ({
      start,
      end,
      updateMode: null,
    }),
  ),
  on(
    rulerActions.setBoundary,
    ({ start, end }, { boundary, timestamp }): RulerState => {
      const [sortedStart, sortedEnd] = [start, end].sort((a, b) => a - b);

      return {
        start: timestamp ?? sortedStart,
        end: timestamp ?? sortedEnd,
        boundary,
        updateMode: 'boundary',
      };
    },
  ),
  on(
    rulerActions.setMoving,
    (state, { timestamp }): RulerState => ({
      ...state,
      previousTimestamp: timestamp,
      updateMode: 'move',
    }),
  ),
  on(rulerActions.setTimestamp, (state, { timestamp }): RulerState => {
    switch (state.updateMode) {
      case 'boundary':
        return {
          ...state,
          [state.boundary]: timestamp,
        };
      case 'move': {
        const delta = state.previousTimestamp - timestamp;
        return {
          ...state,
          start: state.start - delta,
          end: state.end - delta,
          previousTimestamp: timestamp,
        };
      }
      default:
        return state;
    }
  }),
  on(rulerActions.setRange, (state, { start, end }) => ({
    ...state,
    start,
    end,
  })),
  on(
    undoActions.undo,
    undoActions.restore,
    (state, { diff: { ruler } }): RulerState => {
      if (!ruler) {
        return state;
      }
      return { ...state, ...ruler };
    },
  ),
);
